/**
 * reminderDetect — PURE reminder detection + 🔔-line composition.
 *
 * Extracted from useReminderCapture.js so BOTH the legacy <textarea>
 * capture flow AND the new CodeMirror-6 editor can share one copy of
 * the detection rules and the bell-line rewrite. No React, no
 * Firestore, no DOM: given a line of text (and the caller's list-mode
 * flags / prefs-derived default time) these functions decide whether
 * the line is a reminder and what the canonical 🔔 line should read as.
 * They never create reminders or mutate anything — the caller owns all
 * side effects.
 *
 * The constants below are the single source of truth for the bell /
 * comment / continuation / keyword patterns. useReminderCapture.js
 * imports them from here rather than keeping its own copies, so the two
 * editors can never drift.
 */
import { parseReminderLine, buildResolvedDatePhrase } from './reminderParser';
import { parseExistingBellLine } from './parseExistingBellLine';

// Canonical spaced bell prefix used when we COMPOSE a new bell-line.
export const PREFIX = '🔔 ';

// Detection regexes — the trailing whitespace is OPTIONAL. Users can
// type "🔔Wed call mom" with no space and we still pick it up. We only
// canonicalize TO the spaced form (PREFIX) when we COMPOSE new lines;
// we accept either when reading. An optional ↻ between bell and content
// is tolerated for legacy recurring lines. DONE_BELL_RE accepts [DONE]
// (current), 🔕 (round-1 fallback), and ~~🔔 (legacy strikethrough).
export const BELL_RE = /^🔔↻?\s?/;
export const DONE_BELL_RE = /^(?:\[DONE\]|🔕|~~🔔)↻?\s?/;

// Continuation line check — lines starting with whitespace or any of
// the bullet-ish glyphs are treated as notes attached to the reminder
// above (in the designated list), not as new reminders of their own.
export const CONTINUATION_PATTERN = /^[\s\-*·.+>]/;

// Comment-line marker. Lines starting with `//` are treated as
// commentary/notes — never auto-captured as reminders, even in a
// designated list.
export const COMMENT_PATTERN = /^\s*\/\//;

// Markdown header. A section label, never a reminder — see the header
// note on detectReminderOnLine for what capturing one costs.
export const HEADER_PATTERN = /^\s*#{1,6}\s/;

/**
 * NOTE ATTACHMENT — "a line is a note if its paragraph starts with a
 * reminder." Walter 2026-08-25:
 *
 *   "we already separate each event by an extra line break… how about
 *    everything w/o a double line break is a note under the event?"
 *
 * The blank line was already doing the separating work visually; now it does
 * it semantically too, and the rule is one you can SEE. Before this, a note
 * had to be marked — a leading space, "-", "*" — which nobody guesses.
 *
 * Two properties keep existing lists intact:
 *   - a 🔔 / [DONE] line is NEVER a note, blank line or not. Otherwise
 *     back-to-back reminders would swallow each other.
 *   - a paragraph that does NOT start with a reminder is unaffected: every
 *     line in it still captures on a single Enter, exactly as before. That's
 *     Walter's refinement — "unless the line directly above it already is a
 *     reminder" — generalised to the whole paragraph so a two-line note
 *     doesn't need indenting.
 *
 * The old leading-marker continuation rule still works and is still honoured;
 * it's just no longer the only way to write a note.
 */
export function isReminderLine(line) {
  return BELL_RE.test(line || '') || DONE_BELL_RE.test(line || '');
}

/**
 * inReminderParagraph(textBefore) — does the paragraph the NEXT line belongs
 * to already contain a reminder? `textBefore` is everything before that
 * line's start. Walk back to the paragraph boundary: a reminder line means
 * yes, a blank line (or the top of the document) means no.
 */
export function inReminderParagraph(textBefore) {
  const lines = String(textBefore ?? '').split('\n');
  // A textBefore that ends at a line start carries a trailing '' — that's the
  // line we're asking about, not part of the history.
  if (lines.length && lines[lines.length - 1] === '') lines.pop();
  for (let i = lines.length - 1; i >= 0; i--) {
    const ln = lines[i];
    if (ln.trim() === '') return false;
    if (isReminderLine(ln)) return true;
  }
  return false;
}

/** Array form of the same question, for callers that already have lines. */
export function isNoteLine(lines, index) {
  const arr = Array.isArray(lines) ? lines : [];
  const cur = arr[index];
  if (cur == null || cur.trim() === '') return false;
  if (isReminderLine(cur)) return false;
  for (let i = index - 1; i >= 0; i--) {
    const ln = arr[i];
    if (ln.trim() === '') return false;
    if (isReminderLine(ln)) return true;
  }
  return false;
}

// On a NON-designated list, the only lines that auto-become reminders
// are ones starting with an explicit colon-keyword — `reminder:` or
// `todo:`. The `(?=\S)` lookahead requires at least one non-space char
// after the colon. match[0] is the keyword+colon+trailing-space, which
// the capture flow strips before parsing the remainder.
export const GENERAL_LIST_KEYWORD_RE = /^(reminder|todo)\s*:\s*(?=\S)/i;

/**
 * computeReminderLine(line, parse, defaultTime) — given the original
 * raw line + parser result, returns the post-confirm line text (with
 * 🔔 prefix and resolved date/time). Pure: moved verbatim from the
 * hook's computeNewLineForConfirm, with getDefaultTimeFromPrefs()
 * replaced by the passed `defaultTime = { hour, minute }`.
 *
 * Format follows Walter's spec:
 *   "Sunday call pete"        -> "🔔 Sunday 5/3 7am: call pete"
 *   "Sunday 3pm call pete"    -> "🔔 Sunday 5/3 3pm: call pete"
 *   "5/3 call pete"           -> "🔔 5/3 7am: call pete"
 *   "tomorrow call pete"      -> "🔔 tomorrow 5/3 7am: call pete"
 *
 * Anything that's not parsed as dated (intent markers, undated
 * paragraph in designated list) just gets the 🔔 prefix.
 *
 * The canonical form ALWAYS has a colon between the date+time prefix
 * and the task body — but only when the date sits at the START of the
 * line AND there's task content after it. Mid-line dates are left alone.
 */
export function computeReminderLine(line, parse, defaultTime) {
  // Defense: never double-prefix. If the input line already starts with
  // 🔔 (or ~~🔔), return it as-is.
  if (/^\s*(?:~~)?🔔/.test(line || '')) return line;
  if (parse.kind === 'dated' && parse.parsedDate) {
    const trimmed = line.trimStart();
    const wsOffset = line.length - trimmed.length;
    const { hour, minute } = defaultTime;
    const replacement = buildResolvedDatePhrase(
      parse.dateText || '',
      parse.parsedDate,
      !!parse.hasCertainTime,
      hour,
      minute,
      parse.recurrence || null,
      // Walter 2026-09-26: keep a start date he typed visible in the line.
      !!(parse.recurrence && parse.hasCertainDay),
    );
    // Prefer the explicit replace-region (recurrence + end-condition
    // aware). Fall back to dateIndex + dateText.length for older parser
    // shapes if a stale parse object reaches this path.
    let startInTrimmed;
    let endInTrimmed;
    if (typeof parse.replaceStart === 'number' && typeof parse.replaceEnd === 'number') {
      startInTrimmed = parse.replaceStart;
      endInTrimmed = parse.replaceEnd;
    } else {
      startInTrimmed = parse.dateIndex ?? 0;
      endInTrimmed = (parse.dateIndex ?? 0) + (parse.dateText || '').length;
    }
    const di = startInTrimmed + wsOffset;
    const dj = endInTrimmed + wsOffset;
    const before = line.slice(0, di);
    const after = line.slice(dj);
    // Insert ": " when the date sits at the start of the line AND
    // there's task content after. Strip leading punctuation as well as
    // whitespace from the task remainder (users write "tomorrow 3pm —
    // book table"), and strip a leading COLON the user typed after the
    // date ("6/12: cal randy") so the injected ": " doesn't double it.
    const dateAtStart = before.trim() === '';
    const afterTrim = after.replace(/^[\s,;:\-—–]+/, '');
    const hasTaskAfter = afterTrim.length > 0;
    const resolved = dateAtStart && hasTaskAfter
      ? before + replacement + ': ' + afterTrim
      : before + replacement + after;
    return PREFIX + resolved;
  }
  // Undated (intent-marker, or designated-list freeform) — just prepend.
  return PREFIX + line;
}

/**
 * canonicalizeBellLine(line, defaultTime) -> the rewritten line, or null when
 * there is nothing to do.
 *
 * WHY (Walter 2026-09-07). He typed a reminder into a line that already
 * carried a 🔔 - left over from an empty bell line he'd deleted - and
 * concluded it hadn't registered. It had: the doc was correct and the agenda
 * showed `9/16 7am:`. What was missing was the CUE - the editor's orange date
 * (the `md-rdate` decoration in MarkdownEditor) only appears on the canonical
 * form, because it keys off the `": "` that the capture rewrite inserts. In
 * his words: "it's the visual cue we're using for correctly registered dates
 * and times."
 *
 * A plain-bell line whose body reads as dated but isn't canonical is rewritten
 * by the same computeReminderLine every other path uses. Narrow on purpose:
 *   - LEADING plain bell only - not `~~`, the done prefixes, the recurrence
 *     glyph, or a mid-line bell (those encode state or describe bells);
 *   - the body must parse as DATED - an undated bell line is legitimate;
 *   - an already-canonical line returns null, which is also the fixed point
 *     that stops it re-firing on its own output.
 * Text only; the reconciler updates the reminder in place by hash/similarity.
 *
 * First shipped as #225 and reverted: a line vanished during its test. That
 * was traced to the stale-echo adopt bug (#226, on prod), not to this - its
 * branch was inert on the click-away path where the text vanished.
 */
export function canonicalizeBellLine(line, defaultTime) {
  const raw = String(line ?? '');
  // Leading plain bell, optional single space. `~~🔔`, `🔕`, `[DONE]` and
  // `🔔↻` all fail this deliberately.
  // Walter 2026-09-14: the bell may be followed by the invisible emoji
  // selector U+FE0F (Windows' emoji picker writes one). It is part of the
  // bell and is dropped from the canonical line. Without this the selector
  // began the body, no colon was inserted, and the line was altered with a
  // stray "7am" and space but still not tidied. The recurrence guard comes
  // BEFORE the optional selector so backtracking can't sneak past it.
  const m = raw.match(/^(\s*)🔔(?!\uFE0F?↻)\uFE0F? ?/);
  if (!m) return null;
  const body = raw.slice(m[0].length);
  if (!body.trim()) return null;
  // A second bell anywhere means the line describes bells rather than being one.
  if (body.includes('🔔')) return null;
  // Walter 2026-09-26: read as an EXISTING reminder - the line already has
  // its bell, so the question-mark guard (meant to stop a NOTE becoming a
  // reminder) must not stop the tidy-up here. See lib/parseExistingBellLine.
  const parse = parseExistingBellLine(body);
  if (!parse.isReminder || parse.kind !== 'dated' || !parse.parsedDate) return null;
  // Walter 2026-09-15 - THE OVERDUE BUG. The parser rolls a month/day that has
  // already passed into NEXT year (chrono forwardDate), and the date text is
  // then written as M/D/YYYY. So tidying an overdue line such as
  // "🔔 9/5 12pm: test one" produced "🔔 9/5/2027 12pm: test one", and the
  // reconciler moved the reminder a year out - silently, on a plain Enter at
  // the end of the line (on prod since #233). Never put a year he didn't type
  // into his line: the typing path asks him first; this path cannot ask.
  if (parse.yearImputed) return null;
  // A line whose date and time are already followed by its colon already has
  // its receipt - leave it exactly as written, so the tidy-up never reformats
  // a tidy line (for instance, dropping a year he did type).
  const trimmedBody = body.trimStart();
  const dateEnd = typeof parse.replaceEnd === 'number'
    ? parse.replaceEnd
    : (parse.dateIndex ?? 0) + (parse.dateText || '').length;
  if (parse.hasCertainTime && /^:(\s|$)/.test(trimmedBody.slice(dateEnd))) return null;
  const rewritten = m[1] + computeReminderLine(body, parse, defaultTime);
  return rewritten === raw ? null : rewritten;
}

/**
 * bellLineReceipt(line, defaultTime) -> { newLine } | null
 *
 * The capture result for a leftover-bell line. It carries NO `commit`, and
 * that is the point (Walter 2026-09-14, step 3): MarkdownEditor's leave-a-line
 * trigger only applies results that carry a commit - its guard so that merely
 * clicking through old lines never rewrites them - while Enter applies any
 * newLine. So this rewrites a bell line only when he presses Enter on it.
 * A test pins the shape; do not add a commit here without removing that guard
 * on purpose.
 */
export function bellLineReceipt(line, defaultTime) {
  const newLine = canonicalizeBellLine(line, defaultTime);
  return newLine ? { newLine } : null;
}

/**
 * bellLineCaptureResult(line, defaultTime, { leavingEditedLine })
 *   -> { newLine } | { newLine, commit } | null
 *
 * What the live-preview editor's capture returns for a leftover-bell line
 * (Walter 2026-09-14, "i: your recommendation" - track edits):
 *   - Enter (no option)              -> { newLine }; Enter applies any newLine.
 *   - leaving a line he did NOT edit -> { newLine }; the leave-a-line trigger
 *     needs a commit, so it ignores this - clicking through old lines never
 *     rewrites them.
 *   - leaving a line he EDITED       -> { newLine, commit: no-op }; the no-op
 *     commit is only the trigger's permission to apply the text. No document
 *     is created - the reconciler matches the line back to its reminder.
 */
export function bellLineCaptureResult(line, defaultTime, { leavingEditedLine = false } = {}) {
  const receipt = bellLineReceipt(line, defaultTime);
  if (!receipt) return null;
  return leavingEditedLine ? { ...receipt, commit: () => {} } : receipt;
}

/**
 * detectReminderOnLine(lineText, { isDesignatedList }) — the shared
 * reminder-detection core used by both the Enter-time capture and the
 * blur-time batch capture. Returns
 *   { isReminder: true, parse, composeLine }
 * or null.
 *
 * `parse` is the parseReminderLine result (or an undated fallback shape
 * for lines that don't parse as dated but are still reminders in the
 * relevant mode). `composeLine` is the text to pass to
 * computeReminderLine — for a designated line that's the line itself;
 * for a keyword line it's the remainder after the stripped keyword.
 *
 * Encodes ONLY the detection shared by both call sites: skip empty /
 * bell-prefixed / comment lines; in a designated list skip continuation
 * lines and treat every remaining paragraph as a reminder (undated
 * fallback when it doesn't parse as dated); on a general list require a
 * `reminder:` / `todo:` keyword, strip it, and parse the remainder.
 *
 * HEADERS ARE SKIPPED (Walter 2026-08-24). This used to be the one rule
 * the two paths disagreed on: the blur path skipped `#` lines, the
 * Enter path captured them, so pressing Enter at the end of a section
 * header in the Reminders list turned "# OVERDUE" into "🔔 # OVERDUE".
 * That line is then immortal — `organizeReminderList` strips its own old
 * headers by looking for a LEADING `#`, which the bell hides, while the
 * bell makes the line read as an undated reminder — so every subsequent
 * sort re-files it under UNDATED. Walter accumulated ten of them and
 * couldn't delete them permanently. A header is a label, never a thing
 * to be reminded about, in either list mode.
 *
 * The Enter-path-only guards (mid-line 🔔, @-log lines, reminders-enabled
 * / log-list gating) stay at that call site.
 */
export function detectReminderOnLine(lineText, { isDesignatedList, isNote } = {}) {
  const trimmed = (lineText || '').trim();
  if (trimmed.length === 0) return null;
  if (BELL_RE.test(lineText) || DONE_BELL_RE.test(lineText)) return null;
  if (COMMENT_PATTERN.test(lineText)) return null;
  if (HEADER_PATTERN.test(lineText)) return null;
  // This line belongs to a reminder above it — it's a note, not a reminder of
  // its own. Callers decide via inReminderParagraph / isNoteLine.
  if (isNote) return null;

  if (isDesignatedList) {
    // Designated-list rule: every paragraph (every non-continuation
    // line) is a reminder, even if it has no parsed date. Lines
    // starting with whitespace / bullet glyphs are notes attached to
    // the reminder above — skip.
    if (CONTINUATION_PATTERN.test(lineText)) return null;
    // allowQuestion: every non-comment line is a reminder, so a trailing
    // "?" must not block dating. allowEmptyBody: a bare date ("6/6")
    // becomes a DATED, bodyless reminder for that day.
    const p = parseReminderLine(lineText, { allowQuestion: true, allowEmptyBody: true });
    const parse = p.isReminder ? p : {
      isReminder: true,
      kind: 'undated',
      intentMarker: null,
      restText: trimmed,
      recurrence: null,
      confidence: 'low',
    };
    return { isReminder: true, parse, composeLine: lineText };
  }

  // NON-designated list: the ONLY lines that become reminders are ones
  // starting with an explicit colon-keyword (`reminder:` / `todo:`). We
  // strip the keyword and parse the remainder exactly like a
  // designated-list line.
  const kw = lineText.match(GENERAL_LIST_KEYWORD_RE);
  if (!kw) return null;
  const composeLine = lineText.slice(kw[0].length);
  const p = parseReminderLine(composeLine, { allowQuestion: true, allowEmptyBody: true });
  const parse = p.isReminder ? p : {
    isReminder: true,
    kind: 'undated',
    intentMarker: null,
    restText: composeLine.trim(),
    recurrence: null,
    confidence: 'low',
  };
  return { isReminder: true, parse, composeLine };
}
